/**
 * 🌈 RainbowCanonPanel — Placeholder showcase for the upcoming Aura Piece
 * material, Rainbow gear pair, and 3 named Auras (Jackpot / Chronophage's
 * Shield / Tic-for-Tac/Slash).
 *
 * Combat wiring isn't live yet — this is the placeholder viewer so the owner
 * can review names, stats, and multi-tier text before we build the resolvers.
 *
 * Endpoint: GET /api/rainbow-canon/all
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RainbowCanonPanel() {
  const [data, setData] = useState(null);
  const [forge, setForge] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, f] = await Promise.all([
          axios.get(`${API}/rainbow-canon/all`),
          axios.get(`${API}/phoenix-forge/set`),
        ]);
        if (!cancelled) { setData(r.data); setForge(f.data); }
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const mat = data.aura_pieces_material || {};
  const gear = data.rainbow_gear || [];
  const auras = data.aura_catalog || [];

  return (
    <div
      className="mt-6 rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-[#0a001a] via-[#0A0A0A] to-[#1a0022] p-5 shadow-[0_0_30px_rgba(232,121,249,0.20)]"
      data-testid="rainbow-canon-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div
            className="text-2xl font-black tracking-wide bg-gradient-to-r from-red-400 via-yellow-300 via-emerald-300 via-cyan-300 via-indigo-400 to-fuchsia-400 bg-clip-text text-transparent"
            style={{ fontFamily: "Anton, sans-serif" }}
            data-testid="rainbow-canon-title"
          >
            🌈 RAINBOW CANON · PREVIEW
          </div>
          <div className="text-[11px] text-neutral-400 mt-1 max-w-xl leading-relaxed">
            Names + placeholders for the incoming Aura upgrade line. Combat resolvers ship next batch.
          </div>
        </div>
        <div
          className="px-3 py-1.5 rounded-full text-xs bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-200 font-semibold"
          data-testid="rainbow-canon-status"
        >
          🚧 Placeholder Canon
        </div>
      </div>

      {/* Aura Piece material */}
      <div
        className="rounded-xl border border-yellow-500/40 bg-yellow-500/5 p-3 mb-4"
        data-testid="aura-piece-material"
      >
        <div className="flex items-center gap-3">
          <div className="text-4xl">{mat.icon}</div>
          <div className="flex-1">
            <div className="text-lg font-black text-yellow-200">{mat.name}</div>
            <div className="text-[11px] text-neutral-400">{mat.description}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black text-yellow-300">+{mat.exp_per_piece}</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">EXP / piece</div>
          </div>
        </div>
      </div>

      {/* Rainbow Gear */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
          🛡️ Rainbow Gear · Chest + Helm
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="rainbow-gear-grid">
          {gear.map((g) => (
            <GearCard key={g.id} gear={g} />
          ))}
        </div>
      </div>

      {/* Auras */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
          🔮 Equipment Auras · 3 named · <span className="text-fuchsia-300">Slot limit: 1 active</span>
        </div>
        <div className="space-y-2" data-testid="aura-list">
          {auras.map((a) => (
            <AuraRow key={a.id} aura={a} />
          ))}
        </div>
      </div>

      {/* 🔥 Phoenix Forge — 5-piece set placeholder */}
      {forge && <PhoenixForgeCard forge={forge} />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function GearCard({ gear }) {
  return (
    <div
      className="rounded-lg border border-fuchsia-500/30 bg-black/40 p-3"
      data-testid={`rainbow-gear-${gear.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="text-3xl">{gear.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-fuchsia-200 leading-tight">{gear.name}</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">
            {gear.slot} · {gear.rarity}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {Object.entries(gear.stats || {}).map(([k, v]) => (
          <span
            key={k}
            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200"
            data-testid={`gear-stat-${gear.id}-${k}`}
          >
            +{v}{k.includes("pct") ? "%" : ""} {k.replace(/_/g, " ")}
          </span>
        ))}
      </div>
      <div className="text-[10px] text-neutral-500 italic mt-2 leading-relaxed">
        {gear.description}
      </div>
    </div>
  );
}

function AuraRow({ aura }) {
  const isTiered = !!aura.levels_supported;
  return (
    <div
      className="rounded-lg border border-purple-500/30 bg-black/40 p-3"
      data-testid={`aura-${aura.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl">{aura.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-base font-bold text-purple-200">{aura.name}</div>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/50 text-purple-200 uppercase tracking-widest">
              {aura.rarity}
            </span>
            {isTiered && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-500/20 border border-orange-500/50 text-orange-200 uppercase tracking-widest"
                data-testid={`aura-${aura.id}-tiered-tag`}
              >
                🔥 Max Lv {aura.max_level}
              </span>
            )}
          </div>
          <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
            {aura.description}
          </div>
        </div>
      </div>
      {isTiered && (
        <div className="mt-2 space-y-1" data-testid={`aura-${aura.id}-tiers`}>
          {(aura.mechanic?.tiers || []).map((t) => (
            <div
              key={t.level}
              className={
                "text-[10px] rounded border px-2 py-1 flex items-start gap-2 " +
                (t.level === aura.max_level
                  ? "border-amber-400/50 bg-amber-500/10 text-amber-100"
                  : "border-neutral-700 bg-neutral-900/60 text-neutral-300")
              }
              data-testid={`aura-tier-${aura.id}-${t.level}`}
            >
              <span className="font-black text-purple-300">L{t.level}</span>
              <span className="flex-1">{t.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function PhoenixForgeCard({ forge }) {
  const pieces = forge.pieces || [];
  const bonus = forge.set_bonus || {};
  return (
    <div
      className="mt-4 rounded-2xl border border-orange-500/40 bg-gradient-to-br from-[#1a0000] via-black/40 to-[#1a0a00] p-4 shadow-[0_0_28px_rgba(249,115,22,0.25)]"
      data-testid="phoenix-forge-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-xl font-black tracking-wide text-orange-200" style={{ fontFamily: "Anton, sans-serif" }}>
          🔥 THE PHOENIX FORGE · 5-PIECE
        </div>
        <span
          className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 animate-pulse"
          data-testid="phoenix-forge-live"
        >
          🔥 LIVE
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3" data-testid="phoenix-forge-grid">
        {pieces.map((p) => (
          <div
            key={p.id}
            className="rounded-lg border border-orange-500/30 bg-black/40 p-2.5"
            data-testid={`phoenix-forge-piece-${p.slot}`}
          >
            <div className="flex items-start gap-2">
              <div className="text-2xl">{p.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-orange-100 leading-tight">{p.name}</div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">
                  {p.slot} · {p.rarity}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {Object.entries(p.stats || {}).map(([k, v]) => (
                <span
                  key={k}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200"
                >
                  +{v} {k.replace(/_/g, " ")}
                </span>
              ))}
            </div>
            {p.attribute && (
              <div className="mt-2 text-[10px] rounded border border-amber-400/40 bg-amber-500/10 text-amber-100 px-1.5 py-1">
                <b>[{p.attribute.name}]</b> · {p.attribute.text}
              </div>
            )}
            {p.resonance && (
              <div className="mt-1 text-[9px] text-purple-300">
                Resonance: {p.resonance.id} (+{p.resonance.mana_bonus} Mana)
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Set bonus */}
      <div
        className="rounded-lg border border-red-500/40 bg-red-500/10 p-2.5"
        data-testid="phoenix-forge-set-bonus"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg">🔥</span>
          <span className="text-sm font-bold text-red-200">
            5/5 Set Bonus · {bonus.name}
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-500/50 text-red-100 uppercase tracking-widest">
            +{bonus.effect?.pct}% dual dmg × {bonus.duration_rounds}rd
          </span>
        </div>
        <div className="text-[11px] text-neutral-300 mt-1.5 leading-relaxed">
          {bonus.description}
        </div>
      </div>
    </div>
  );
}
